// Rail step "Vydání mobilní aplikace" - the BuddyPro lifecycle, 1:1 (F137).
//
// 🔴 Pavel 2026-08-11: "Musíme to udělat stejně, jako se to dělá v BuddyPro.
// Úplně stejně." So this page shows ONE state of ONE state machine, the
// platform's own, and nothing else. What it used to show - a four-row checklist
// of "Sestavení aplikace", "Kontrola v App Store", "Kontrola v Google Play" -
// described a process nobody runs: the platform has no per-store tracking at
// all, its team builds AND submits under a single `in_publishing`, and Google
// Play is one boolean of the wizard plus a link afterwards.
//
// The Apple account is NOT here. It is the client's own confirmation on the
// Apple step (S21) and repeating it turned this page into a second copy of
// that one (the F135 leak, in the other direction).
//
// Nothing on this page is a control. Pavel's rule for who clicks what: "kde
// výsledek vidí ZÁKAZNÍK, odklikává zákazník; kde ho vidíme MY, odklikává
// parťák." Nobody but us can see a store review, so the client reads a calm
// description and the partner moves the state from their own workspace.
import { WorkingAvatar } from "../../WorkingAvatar";
import { ActionError, DataError, isRecord } from "../shared";
import { APP_RELEASE_CLIENT_TONE, isAppReleaseStatus } from "../../../shared/app-release";
import { appStateValue, type AppTrackProps } from "./app-track-shared";

export function ReleaseStep(props: AppTrackProps) {
  const appState = appStateValue(props);
  // ONE fact, derived on the server (worker/src/app-release-state.ts) and read
  // here. The browser cannot derive it - the wizard's submitted flag and the
  // app_live milestone are not on this screen - and a second half-derivation is
  // how a page ends up saying two things about one app.
  const stored = isRecord(appState?.release) ? appState.release.status : undefined;
  const status = isAppReleaseStatus(stored) ? stored : "draft";
  const tone = APP_RELEASE_CLIENT_TONE[status];
  const storeUrls = isRecord(appState?.store_urls) ? appState.store_urls : {};
  const iosUrl = typeof storeUrls.ios === "string" ? storeUrls.ios : null;
  const androidUrl = typeof storeUrls.android === "string" ? storeUrls.android : null;

  return (
    <section className="m5b-view app-step-view" data-app-step="release" aria-labelledby="app-release-title">
      <header className="m5b-heading">
        <p className="eyebrow blue-eyebrow">{props.t("app.release.eyebrow")}</p>
        <h1 id="app-release-title">{props.t("rail.steps.app_release")}</h1>
        <p className="lead">{props.t("app.release.focusCopy")}</p>
      </header>
      {props.dataErrors?.has("resources") && <DataError t={props.t} />}

      <article
        className={`app-release-status tone-${tone}`}
        data-sales-step="release"
        data-release-status={status}
        role="status"
      >
        {/* F53: every state that says the team is working wears the ONE working
            symbol, never a shape of its own. */}
        {tone === "working" && (
          <WorkingAvatar size="card" profilePhotoUrl={props.profilePhotoUrl} />
        )}
        <div className="app-release-status-text">
          <h2>{props.t(`app.release.status.${status}.title`)}</h2>
          <p>{props.t(`app.release.status.${status}.copy`)}</p>
          {(iosUrl || androidUrl) && (
            <ul className="app-release-links">
              {iosUrl && (
                <li>
                  <a className="secondary-button" href={iosUrl} target="_blank" rel="noreferrer">
                    {props.t("app.release.openAppStore")}
                  </a>
                </li>
              )}
              {androidUrl && (
                <li>
                  <a className="secondary-button" href={androidUrl} target="_blank" rel="noreferrer">
                    {props.t("app.release.openGooglePlay")}
                  </a>
                </li>
              )}
            </ul>
          )}
        </div>
      </article>

      {/* Kept from F135: the one thing about Google Play the client would
          otherwise go looking for an account to do. */}
      <p className="app-google-play-note">{props.t("app.release.googlePlayPublisher")}</p>
      <ActionError t={props.t} issue={props.actionError} />
    </section>
  );
}
